import { HunterTreadCorner, HunterTreadDepth } from "../../queries/useGetHunterTire";

/**
 * The four corners every passenger vehicle reports, in the order the grid lays
 * them out. Multi-axle readings (axle3_left and friends) are kept by the API but
 * fall outside the grid.
 */
export const TREAD_DEPTH_CORNERS: { key: string; label: string; name: string }[] =
  [
    { key: "front_left", label: "FL", name: "Front Left" },
    { key: "front_right", label: "FR", name: "Front Right" },
    { key: "rear_left", label: "RL", name: "Rear Left" },
    { key: "rear_right", label: "RR", name: "Rear Right" },
  ];

export type TreadSeverity = "fail" | "ok" | "review";

export type TreadCornerDisplay = {
  display: string;
  position: null | string;
  reported: null | string;
  severity: TreadSeverity | null;
};

/**
 * Hunter grades every reading itself: 1 pass, 2 review, 3 fail.
 */
const SEVERITY_BY_RESULT: Record<string, TreadSeverity> = {
  "1": "ok",
  "2": "review",
  "3": "fail",
};

/**
 * Reduce one stored corner to what the column renders.
 *
 * Readings arrive in whatever unit the equipment reported — fractional 32nds
 * from one machine, decimal inches or millimetres from the next — so everything
 * is normalised to 32nds, which is what a technician reads off a depth gauge.
 * The value as Hunter actually reported it is carried along for the tooltip.
 */
export const formatTreadCorner = (
  corner: HunterTreadCorner | undefined,
): TreadCornerDisplay | null => {
  const min = corner?.min;

  if (!min || typeof min.inches !== "number" || Number.isNaN(min.inches)) {
    return null;
  }

  const reported = [min.value, min.unit].filter(Boolean).join(" ").trim();

  return {
    display: `${Math.round(min.inches * 32)}/32`,
    position: min.position ?? null,
    reported: reported || null,
    severity: min.result ? (SEVERITY_BY_RESULT[min.result] ?? null) : null,
  };
};

/**
 * Whether a payload carried any reading at all. Most aligners send none.
 */
export const hasTreadReadings = (
  treadDepth: HunterTreadDepth | null | undefined,
): boolean =>
  !!treadDepth &&
  Object.values(treadDepth).some((corner) => !!formatTreadCorner(corner));

/**
 * Rim size for display, or null when there is nothing worth showing.
 *
 * Hunter sends -1 when the machine could not size the rim, and rows written
 * before that was guarded at ingest still hold it — so it is suppressed here
 * too rather than rendering as a -1 inch rim. Half sizes are real (16.5" and
 * 19.5" turn up on light trucks), so only the trailing zero is trimmed.
 */
export const formatRimSize = (
  diameter: null | number | string | undefined,
  unit: null | string | undefined,
): null | string => {
  const value = rimDiameterValue(diameter);

  if (value === null) {
    return null;
  }

  return `${parseFloat(value.toFixed(1))} ${unit || "inch"}`;
};

/**
 * Rim diameter as a sortable number, or null when there is nothing real to
 * sort on. Shares the sentinel guard with formatRimSize so the column never
 * sorts on a value it refuses to display.
 */
export const rimDiameterValue = (
  diameter: null | number | string | undefined,
): null | number => {
  const value = typeof diameter === "string" ? parseFloat(diameter) : diameter;

  if (typeof value !== "number" || Number.isNaN(value) || value <= 0) {
    return null;
  }

  return value;
};

/**
 * The shallowest reading across the four grid corners — what decides whether
 * the vehicle needs tires, and what the CMS sorts on, so both surfaces order
 * the column the same way. Deliberately ignores axles beyond the second: the
 * grid does not show them, and sorting on an invisible value reads as a bug.
 */
export const worstTreadInches = (
  treadDepth: HunterTreadDepth | null | undefined,
): null | number => {
  if (!treadDepth) {
    return null;
  }

  const readings = TREAD_DEPTH_CORNERS.map(
    ({ key }) => treadDepth[key]?.min?.inches,
  ).filter(
    (inches): inches is number =>
      typeof inches === "number" && !Number.isNaN(inches),
  );

  return readings.length ? Math.min(...readings) : null;
};
